import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { gamificationApi, showError } from "../lib";
import type { LeaderboardEntry } from "../lib";
import {
  Award,
  Star,
  Trophy,
  Zap,
  Lock,
  Loader,
  Crown,
  Medal,
  Sparkles,
  BookOpen,
  Flame,
  TrendingUp,
  Gift,
  Diamond,
  Coffee,
  ChevronRight,
  Users,
  Target,
} from "lucide-react";

interface Badge {
  _id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
  category: string;
  points: number;
  rarity?: string;
  requirement?: {
    type: string;
    count: number;
  };
}

interface UserBadge {
  _id: string;
  badge: Badge;
  earnedAt: string;
}

interface UserStats {
  totalPoints: number;
  level: number;
  rank: number;
  badgesCount: number;
  coursesCompleted: number;
  quizzesPassed: number;
  streakDays: number;
  totalHoursLearned: number;
  progressToNextLevel?: number;
  nextLevel?: { title: string; pointsNeeded: number } | null;
  currentStreak?: number;
  longestStreak?: number;
}

const getRarityColor = (rarity?: string) => {
  const colors: Record<string, string> = {
    common: "from-gray-50 to-gray-100 border-gray-200",
    uncommon: "from-green-50 to-green-100 border-green-200",
    rare: "from-blue-50 to-blue-100 border-blue-200",
    epic: "from-purple-50 to-purple-100 border-purple-200",
    legendary: "from-amber-50 to-amber-100 border-amber-200",
  };
  return colors[rarity || "common"] || colors.common;
};

const getRarityBadgeColor = (rarity?: string) => {
  const colors: Record<string, string> = {
    common: "bg-gray-500",
    uncommon: "bg-green-500",
    rare: "bg-blue-500",
    epic: "bg-purple-500",
    legendary: "bg-amber-500",
  };
  return colors[rarity || "common"] || colors.common;
};

const getRarityText = (rarity?: string) => {
  const texts: Record<string, string> = {
    common: "Common",
    uncommon: "Uncommon",
    rare: "Rare",
    epic: "Epic",
    legendary: "Legendary",
  };
  return texts[rarity || "common"] || "Common";
};

const BadgesPage = () => {
  const [myBadges, setMyBadges] = useState<UserBadge[]>([]);
  const [allBadges, setAllBadges] = useState<Badge[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"earned" | "available" | "quests">("earned");
  const [dailyQuests, setDailyQuests] = useState<any[]>([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [myBadgesRes, allBadgesRes, statsRes, leaderboardRes, questsRes] = await Promise.all([
        gamificationApi.getMyBadges(),
        gamificationApi.getAllBadges(),
        gamificationApi.getMyStats(),
        gamificationApi.getLeaderboard(10),
        gamificationApi.getDailyQuests().catch(() => ({ success: false, data: [] })),
      ]);

      setMyBadges(myBadgesRes?.data || []);
      setAllBadges(allBadgesRes?.data || []);
      setLeaderboard(leaderboardRes?.data || []);
      setDailyQuests(questsRes?.data || []);
      
      if (statsRes?.success && statsRes?.data) {
        const responseData = statsRes.data as any;
        const statsDoc = responseData.stats || {};
        const extractedStats: UserStats = {
          totalPoints: statsDoc.totalPoints || responseData.totalPoints || 0,
          level: responseData.level?.level || responseData.level || 1,
          rank: statsDoc.rank || responseData.rank || 0,
          badgesCount: statsDoc.badges?.length || responseData.badgesCount || 0,
          coursesCompleted: statsDoc.coursesCompleted || 0,
          quizzesPassed: statsDoc.quizzesPassed || 0,
          streakDays: statsDoc.longestStreak || statsDoc.currentStreak || 0,
          totalHoursLearned: statsDoc.lessonsWatched || 0,
          currentStreak: statsDoc.currentStreak || 0,
          longestStreak: statsDoc.longestStreak || 0,
          progressToNextLevel: responseData.progressToNextLevel || 0,
          nextLevel: responseData.nextLevel || null,
        };
        setStats(extractedStats);
      }
    } catch (err) {
      console.error("Failed to fetch badges:", err);
      setMyBadges([]);
      setAllBadges([]);
      setLeaderboard([]);
      setDailyQuests([]);
    } finally {
      setLoading(false);
    }
  };

  const safeMyBadges = Array.isArray(myBadges) ? myBadges : [];
  const safeAllBadges = Array.isArray(allBadges) ? allBadges : [];

  const getLevelValue = (value: any) => {
    if (value && typeof value === 'object' && value.level !== undefined) return value.level;
    if (typeof value === 'number') return value;
    return 1;
  };

  // Helper function to get user display name from leaderboard entry
  const getUserDisplayName = (entry: LeaderboardEntry): string => {
    const user = entry.user;
    if (!user) return `Learner #${entry.rank}`;
    
    // Check for firstName/lastName
    if (user.firstName || user.lastName) {
      const firstName = user.firstName || "";
      const lastName = user.lastName || "";
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName) return fullName;
    }
    
    // Check for name field
    if ((user as any).name) return (user as any).name;
    
    // Check for username
    if ((user as any).username) return (user as any).username;
    
    // Check for email
    if ((user as any).email) return (user as any).email.split('@')[0];
    
    // Fallback
    return `Learner #${entry.rank}`;
  };

  const earnedBadgeIds = new Set(
    safeMyBadges
      .filter((ub) => ub && ub.badge && ub.badge._id)
      .map((ub) => ub.badge._id)
  );
  const availableBadges = safeAllBadges.filter((b) => b && b._id && !earnedBadgeIds.has(b._id));

  const displayedBadges = activeTab === "earned" 
    ? safeMyBadges.filter((ub) => ub && ub.badge && ub.badge._id)
    : availableBadges;

  const getLevelProgress = () => {
    if (!stats) return { current: 0, next: 100, percent: 0 };
    return {
      current: stats.totalPoints % 100,
      next: 100,
      percent: stats.progressToNextLevel || (stats.totalPoints % 100),
    };
  };

  const levelProgress = getLevelProgress();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="text-center">
          <Loader className="w-8 h-8 mx-auto mb-3 text-orange-500 animate-spin" />
          <p className="text-[11px] text-gray-500">Loading achievements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-6 bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
      <div className="px-4 mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-1">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg shadow-sm bg-gradient-to-r from-orange-600 to-orange-500">
              <Award className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-lg font-bold text-gray-900">Achievements</h1>
          </div>
          <p className="text-[10px] text-gray-500 ml-10">Track badges, level up & earn rewards</p>
        </div>

        {/* Stats Overview Card */}
        {stats && (
          <div className="mb-4 overflow-hidden bg-white border border-gray-100 shadow-sm rounded-xl">
            <div className="p-4">
              <div className="flex items-center gap-4">
                {/* Level Badge */}
                <div className="relative flex-shrink-0">
                  <div className="flex items-center justify-center w-16 h-16 rounded-full shadow-md bg-gradient-to-br from-orange-600 to-orange-500 ring-3 ring-white">
                    <div className="text-center">
                      <span className="text-2xl font-bold text-white">{stats.level}</span>
                      <p className="text-[8px] text-white/80 font-semibold">LVL</p>
                    </div>
                  </div>
                  {stats.rank <= 10 && (
                    <div className="absolute flex items-center justify-center w-5 h-5 rounded-full -top-1 -right-1 bg-amber-400 ring-2 ring-white">
                      <Crown className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>

                {/* Quick Stats */}
                <div className="grid flex-1 grid-cols-3 gap-2">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-0.5 mb-0.5">
                      <Zap className="w-3 h-3 text-amber-500" />
                      <span className="text-sm font-bold text-gray-900">{stats.totalPoints}</span>
                    </div>
                    <p className="text-[9px] text-gray-500">Points</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-0.5 mb-0.5">
                      <Award className="w-3 h-3 text-purple-500" />
                      <span className="text-sm font-bold text-gray-900">{safeMyBadges.length}</span>
                    </div>
                    <p className="text-[9px] text-gray-500">Badges</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-0.5 mb-0.5">
                      <Flame className="w-3 h-3 text-orange-500" />
                      <span className="text-sm font-bold text-gray-900">{stats.streakDays}</span>
                    </div>
                    <p className="text-[9px] text-gray-500">Streak</p>
                  </div>
                </div>
              </div>

              {/* Level Progress Bar */}
              <div className="mt-3">
                <div className="flex justify-between mb-1 text-[9px]">
                  <span className="font-medium text-gray-600">Level {stats.level} → {stats.level + 1}</span>
                  <span className="text-gray-500">{levelProgress.current}/100 pts</span>
                </div>
                <div className="h-1.5 overflow-hidden bg-gray-100 rounded-full">
                  <div
                    className="h-full transition-all duration-500 rounded-full bg-gradient-to-r from-orange-600 to-orange-500"
                    style={{ width: `${levelProgress.percent}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setActiveTab("earned")}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg transition-all duration-200 ${
              activeTab === "earned"
                ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-sm"
                : "bg-white text-gray-600 border border-gray-200"
            }`}
          >
            <Award className="w-3 h-3" />
            Earned
            <span className={`ml-0.5 px-1 py-0.5 text-[9px] rounded ${
              activeTab === "earned" 
                ? "bg-white/20 text-white" 
                : "bg-gray-100 text-gray-600"
            }`}>
              {safeMyBadges.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab("available")}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg transition-all duration-200 ${
              activeTab === "available"
                ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-sm"
                : "bg-white text-gray-600 border border-gray-200"
            }`}
          >
            <Sparkles className="w-3 h-3" />
            Available
            <span className={`ml-0.5 px-1 py-0.5 text-[9px] rounded ${
              activeTab === "available" 
                ? "bg-white/20 text-white" 
                : "bg-gray-100 text-gray-600"
            }`}>
              {availableBadges.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab("quests")}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg transition-all duration-200 ${
              activeTab === "quests"
                ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-sm"
                : "bg-white text-gray-600 border border-gray-200"
            }`}
          >
            <Target className="w-3 h-3" />
            Quests
            <span className={`ml-0.5 px-1 py-0.5 text-[9px] rounded ${
              activeTab === "quests" 
                ? "bg-white/20 text-white" 
                : "bg-gray-100 text-gray-600"
            }`}>
              {dailyQuests.length}
            </span>
          </button>
        </div>

        {/* Content based on active tab */}
        {activeTab === "quests" ? (
          // Quests Tab Content
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {dailyQuests.length === 0 ? (
              <div className="py-10 text-center bg-white border border-gray-100 shadow-sm rounded-xl col-span-full">
                <Target className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <h3 className="mb-1 text-sm font-semibold text-gray-900">No active quests</h3>
                <p className="text-[10px] text-gray-500">Check back later for new challenges</p>
              </div>
            ) : (
              dailyQuests.map((q: any) => {
                const quest = q.quest || q;
                const progress = q.progress ?? 0;
                const target = q.target ?? quest.requirement?.count ?? 1;
                const status = q.status ?? "active";
                const percent = Math.min(100, Math.round((progress / target) * 100));

                return (
                  <div
                    key={q._id || quest._id}
                    className={`p-4 rounded-xl border ${
                      status === "completed"
                        ? "bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200"
                        : "bg-white border-gray-100"
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex items-center justify-center w-10 h-10 text-xl rounded-lg bg-orange-50">
                        {quest.icon || "🎯"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-gray-900 truncate">{quest.title}</p>
                        <p className="text-[10px] text-gray-500 truncate">{quest.description}</p>
                      </div>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-2">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-orange-500 to-amber-500"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-gray-500">
                      <span>{progress}/{target}</span>
                      <span className="font-semibold text-amber-600">+{quest.xpReward || 0} XP</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        ) : (
          // Badges Tab Content (Earned/Available)
          displayedBadges.length === 0 ? (
            <div className="py-12 text-center bg-white border border-gray-100 shadow-sm rounded-xl">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br from-gray-100 to-gray-200">
                {activeTab === "earned" ? (
                  <Award className="w-8 h-8 text-gray-400" />
                ) : (
                  <Gift className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <h3 className="mb-1 text-sm font-semibold text-gray-900">
                {activeTab === "earned" ? "No badges yet" : "All badges unlocked!"}
              </h3>
              <p className="text-[10px] text-gray-500 max-w-xs mx-auto">
                {activeTab === "earned"
                  ? "Complete courses and pass quizzes to earn badges"
                  : "Amazing! You've collected every badge!"}
              </p>
              {activeTab === "earned" && (
                <div className="flex justify-center gap-2 mt-4">
                  <Link
                    to="/courses"
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500"
                  >
                    <BookOpen className="w-3 h-3" />
                    Explore
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {activeTab === "earned"
                ? safeMyBadges.map((userBadge) => {
                    const badge = userBadge.badge;
                    const rarityBadgeColor = getRarityBadgeColor(badge?.rarity);
                    const rarityText = getRarityText(badge?.rarity);
                    
                    return (
                      <div
                        key={userBadge._id}
                        className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5"
                      >
                        <div className="text-center">
                          <div className="relative inline-block mx-auto mb-2">
                            <div className="flex items-center justify-center shadow-sm w-14 h-14 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl">
                              <span className="text-3xl">{badge?.icon || "🏆"}</span>
                            </div>
                            <div className={`absolute -top-1 -right-1 w-5 h-5 ${rarityBadgeColor} rounded-full flex items-center justify-center shadow-sm`}>
                              <Diamond className="w-2.5 h-2.5 text-white" />
                            </div>
                          </div>
                          <h3 className="text-[11px] font-bold text-gray-900 mb-0.5 line-clamp-1">{badge?.name}</h3>
                          <p className="text-[9px] text-gray-500 mb-1.5 line-clamp-2">{badge?.description}</p>
                          <div className="flex items-center justify-center gap-1 mb-1">
                            <Star className="w-2.5 h-2.5 text-amber-500 fill-amber-500" />
                            <span className="text-[10px] font-semibold text-gray-700">{badge?.points}</span>
                          </div>
                          <p className="text-[8px] text-gray-400 mb-1">
                            {new Date(userBadge.earnedAt).toLocaleDateString()}
                          </p>
                          <span className={`inline-block px-1.5 py-0.5 rounded-full text-[8px] font-semibold ${rarityBadgeColor} text-white`}>
                            {rarityText}
                          </span>
                        </div>
                      </div>
                    );
                  })
                : availableBadges.map((badge) => {
                    const rarityBadgeColor = getRarityBadgeColor(badge?.rarity);
                    const rarityText = getRarityText(badge?.rarity);
                    
                    return (
                      <div
                        key={badge._id}
                        className="p-3 bg-white border-2 border-gray-100 border-dashed rounded-xl opacity-70"
                      >
                        <div className="text-center">
                          <div className="relative inline-block mx-auto mb-2">
                            <div className="flex items-center justify-center bg-gray-100 w-14 h-14 rounded-xl">
                              <Lock className="w-5 h-5 text-gray-400" />
                            </div>
                            <div className={`absolute -top-1 -right-1 w-5 h-5 ${rarityBadgeColor} rounded-full flex items-center justify-center opacity-50`}>
                              <Diamond className="w-2.5 h-2.5 text-white" />
                            </div>
                          </div>
                          <h3 className="text-[11px] font-bold text-gray-700 mb-0.5 line-clamp-1">{badge.name}</h3>
                          <p className="text-[9px] text-gray-500 mb-1.5 line-clamp-2">{badge.description}</p>
                          <div className="flex items-center justify-center gap-1 mb-1">
                            <Star className="w-2.5 h-2.5 text-gray-300" />
                            <span className="text-[10px] text-gray-400">{badge.points}</span>
                          </div>
                          <div className="text-[8px] text-gray-400 mb-1">
                            {badge.requirement && badge.requirement.type
                              ? (badge.requirement.type === "courses_completed"
                                  ? `📚 ${badge.requirement.count} courses`
                                  : badge.requirement.type === "quizzes_passed"
                                  ? `✅ ${badge.requirement.count} quizzes`
                                  : badge.requirement.type === "streak_days"
                                  ? `🔥 ${badge.requirement.count} days`
                                  : `🎯 ${badge.requirement.count}`)
                              : "🔒 Keep learning"}
                          </div>
                          <span className="inline-block px-1.5 py-0.5 rounded-full text-[8px] font-semibold bg-gray-200 text-gray-600">
                            {rarityText}
                          </span>
                        </div>
                      </div>
                    );
                  })}
            </div>
          )
        )}

        {/* Leaderboard Section */}
        {leaderboard.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <Trophy className="w-4 h-4 text-amber-500" />
                <h2 className="text-sm font-semibold text-gray-900">Top Learners</h2>
              </div>
              <Link to="/leaderboard" className="text-[9px] text-orange-500 hover:text-orange-600 flex items-center gap-0.5">
                View all <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="overflow-hidden bg-white border border-gray-100 shadow-sm rounded-xl">
              {leaderboard.slice(0, 5).map((entry, index) => {
                const userName = getUserDisplayName(entry);
                const levelValue = getLevelValue(entry.level);
                
                return (
                  <div key={entry.user?._id || index} className="flex items-center justify-between p-3 border-b border-gray-50 last:border-0">
                    <div className="flex items-center gap-2">
                      <div className={`flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold ${
                        index === 0 ? "bg-amber-100 text-amber-700" :
                        index === 1 ? "bg-gray-100 text-gray-700" :
                        index === 2 ? "bg-orange-100 text-orange-700" :
                        "bg-gray-50 text-gray-500"
                      }`}>
                        {index === 0 ? <Crown className="w-3 h-3" /> : index + 1}
                      </div>
                      <div>
                        <p className="text-[11px] font-medium text-gray-900 truncate max-w-[120px] sm:max-w-[180px]">
                          {userName}
                        </p>
                        <p className="text-[9px] text-gray-500">Level {levelValue}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[11px] font-bold text-gray-900">{entry.totalPoints?.toLocaleString() || 0} pts</p>
                      <p className="text-[8px] text-gray-500">{entry.badgesCount || 0} badges</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Achievement Progress Footer */}
        {safeMyBadges.length > 0 && safeAllBadges.length > 0 && (
          <div className="p-3 mt-6 border border-orange-100 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 bg-orange-100 rounded-full">
                  <Medal className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <p className="text-[9px] text-gray-500">Collection Progress</p>
                  <p className="text-[11px] font-semibold text-gray-900">
                    {safeMyBadges.length} / {safeAllBadges.length}
                  </p>
                </div>
              </div>
              <div className="flex-1">
                <div className="h-1.5 overflow-hidden bg-gray-200 rounded-full">
                  <div
                    className="h-full transition-all duration-500 rounded-full bg-gradient-to-r from-orange-600 to-orange-500"
                    style={{ width: `${(safeMyBadges.length / safeAllBadges.length) * 100}%` }}
                  />
                </div>
              </div>
              <p className="text-[10px] font-semibold text-orange-600">
                {Math.round((safeMyBadges.length / safeAllBadges.length) * 100)}%
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BadgesPage;